import {Markers} from './MapService';
import {getStore} from './store';
import {NEW_BUILDING_ID} from '@/lib/symbols';

/**
 * @typedef {string} HandlerName A name identifying a specific event handler.
 * @typedef {Function} Handler A callback function registered to handle events from EventService.
 * @typedef {Map<HandlerName, Handler>} HandlerMap A Map of event handlers by name.
 *
 * @typedef {string} UID Unique ID that identifies an object's registered event handlers.
 * @typedef {string} EventType A type of event that can be handled.
 *
 * @typedef {(payload: {domEvent: MouseEvent, markers: import('./MapService/Markers').MarkerData[]}) => void}
 *   ContextMenuHandler A callback for right-clicks on the map, given the markers under the pointer.
 */

// The maximum number of marker tabs that can be displayed at once
const MAX_MARKER_TABS = 5;

const validateNonEmptyString = (varName, value) => {
  if (!value || typeof value !== 'string') {
    throw new TypeError(`${varName} must be a non-empty string; got "${value}".`);
  }
};

const validateUid = (uid) => validateNonEmptyString('uid', uid);
const validateHandlerName = (handlerName) => validateNonEmptyString('handlerName', handlerName);

/**
 * Checks if the current tab is a new marker or label
 * @return {boolean} True if the current tab is a new item
 */
const isNewItem = () => {
  const tabId = getStore().state?.infowindow?.infoWindowContents?.[0]?.id;
  return tabId === NEW_BUILDING_ID;
};

class EventService {
  /**
   * Constructs a new EventService and initializes its Map.
   * @constructor
   */
  constructor() {
    /** @type {Map<UID, Map<HandlerName, Handler>>}  */
    this.uidMap = new Map();
    /** @type {Map<UID, Map<HandlerName, Handler>>}  */
    this.layerMap = new Map();
    /** @type {Map<HandlerName, ContextMenuHandler>} */
    this.contextMenuHandlers = new Map();
    /** @type {((markers: object[], event: MouseEvent) => void) | null} */
    this.markerClickHandler = null;
  }

  /**
   * Routes every marker click to one handler instead of the markers' own click callbacks, until cleared with
   * null — in comp picker mode a click toggles the marker's comps rather than opening its infowindow.
   * @param {((markers: object[], event: MouseEvent) => void) | null} handler
   */
  setMarkerClickHandler(handler) {
    this.markerClickHandler = handler;
  }

  /**
   * Registers a new event handler to the Event Service.
   * @param {UID} uid A unique identifier for the object adding a handler.
   * @param {HandlerName} handlerName A name identifying the handler.
   * @param {Handler} handler A function to be invoked to handle events.
   * @throws {TypeError} If `uid` or `handlerName` fails type validation.
   */
  addHandler(uid, handlerName, handler) {
    validateUid(uid);
    validateHandlerName(handlerName);

    if (this.uidMap.has(uid)) {
      const handlerMap = this.uidMap.get(uid);
      if (handlerMap.has(handlerName)) {
        console.warn(`EventService.addHandler: existing handler "${handlerName}" for object "${uid}" is being overwritten.`);
      }
      handlerMap.set(handlerName, handler);
    } else {
      const handlerMap = new Map([[handlerName, handler]]);
      this.uidMap.set(uid, handlerMap);
    }
  }

  /**
   * Registers a new event handler for a specific layer to the Event Service.
   * @param {string} layerName The name of the layer the handler is for.
   * @param {HandlerName} handlerName A name identifying the handler.
   * @param {Handler} handler A function to be invoked to handle events.
   * @throws {TypeError} If `layerName` or `handlerName` fails type validation.
   */
  addLayerHandler(layerName, handlerName, handler) {
    validateNonEmptyString('layerName', layerName);
    validateHandlerName(handlerName);

    if (this.layerMap.has(layerName)) {
      const handlerMap = this.layerMap.get(layerName);
      if (handlerMap.has(handlerName)) {
        console.warn(`EventService.addHandler: existing handler "${handlerName}" for layer "${layerName}" is being overwritten.`);
      }
      handlerMap.set(handlerName, handler);
    } else {
      const handlerMap = new Map([[handlerName, handler]]);
      this.layerMap.set(layerName, handlerMap);
    }
  }

  /**
   * Registers a handler for right-clicks on the map.
   * @param {HandlerName} handlerName
   * @param {ContextMenuHandler} handler
   * @throws {TypeError} If `handlerName` fails type validation.
   */
  addContextMenuHandler(handlerName, handler) {
    validateHandlerName(handlerName);
    if (this.contextMenuHandlers.has(handlerName)) {
      console.warn(`EventService.addContextMenuHandler: existing handler "${handlerName}" is being overwritten.`);
    }
    this.contextMenuHandlers.set(handlerName, handler);
  }

  /**
   * @param {HandlerName} handlerName
   * @return {boolean} `true` if the handler existed and was removed; otherwise `false`.
   * @throws {TypeError} If `handlerName` fails type validation.
   */
  removeContextMenuHandler(handlerName) {
    validateHandlerName(handlerName);
    return this.contextMenuHandlers.delete(handlerName);
  }

  /**
   * Removes an event handler from the EventService.
   * @param {UID} uid The unique identifier for the object removing a handler.
   * @param {HandlerName} handlerName The name identifying the handler to be removed.
   * @return {boolean} `true` if the handler existed and was removed; otherwise `false`.
   * @throws {TypeError} If `uid` or `handlerName` fails type validation.
   */
  removeHandler(uid, handlerName) {
    validateUid(uid);
    validateHandlerName(handlerName);

    if (!this.uidMap.has(uid)) return false;
    return this.uidMap.get(uid).delete(handlerName);
  }

  /**
   * Convenenience method for removing all handlers from the EventService.
   */
  removeAllHandlers() {
    this.uidMap.clear();
  }

  /**
   * Retrieves an already-registered event handler from the EventService.
   * @param {UID} uid The unique identifier for the object whose handler to retrieve.
   * @param {HandlerMap} handlerName The name identifying the handler to be retrieved.
   * @return {Handler | undefined} The retrieved handler or `undefined` if it could not be found.
   * @throws {TypeError} If `uid` or `handlerName` fails type validation.
   */
  getHandler(uid, handlerName) {
    validateUid(uid);
    validateHandlerName(handlerName);

    return this.uidMap.get(uid)?.get(handlerName);
  }

  /**
   * Handles a click on the map
   * @param {google.maps.MapMouseEvent} event
   */
  handleMapClick(event) {
    if (isNewItem()) return;

    getStore().dispatch('infowindow/closeInfoWindow');

    // handle events from topmost feature to bottommost layer
    this._handleMarkers(event.domEvent);
    this._handleFeatures(event.domEvent);
    this._handleLayers(event);
  }

  /**
   * A right-click is only a menu trigger: unlike handleMapClick it leaves the info window alone and fires no
   * marker click handlers.
   * @param {google.maps.MapMouseEvent} event
   */
  handleMapContextMenu(event) {
    if (this.contextMenuHandlers.size === 0) return;
    const {domEvent} = event;
    const markers = Markers.getMarkersAtPosition({x: domEvent.pageX, y: domEvent.pageY});
    this.contextMenuHandlers.forEach((handler) => handler({domEvent, markers}));
  }

  /**
   * Triggers events for every marker clicked at this location.
   * @param {MouseEvent} event
   */
  _handleMarkers(event) {
    const markers = Markers.getMarkersAtPosition({x: event.pageX, y: event.pageY}, MAX_MARKER_TABS);
    if (this.markerClickHandler) {
      if (markers.length) {
        this.markerClickHandler(markers, event);
      }
      return;
    }
    // A staging marker has only dragend, and its click must not stop the markers beneath it
    Promise.all(markers.map((marker) => marker?.events?.find((item) => item.event === 'click')?.callback(event)));
  }

  /**
   * Triggers events for every layer clicked at this location
   * @param {google.maps.MapMouseEvent} event
   */
  _handleLayers(event) {
    this.layerMap.forEach((layerHandlersMap, layerName) => {
      layerHandlersMap.forEach((handler, handlerName) => {
        handler(event);
      });
    });
  }

  /**
   * Triggers events for every feature clicked at this location
   * @param {MouseEvent} event
   */
  _handleFeatures(event) {
    // Get an array of clicked map elements
    const clickedElements = document
      .elementsFromPoint(event.pageX, event.pageY);

    // Get an array of clicked elements with feature ids attached
    const clickedFeatureIds = clickedElements
      .map((element) => {
        return element.ownerSVGElement?.id ?? element.id;
      })
      .filter((id) => {
        // TODO: Attach id to features/graphics as 'featureId' instead of 'id' so that
        // elements with other types of ids aren't intercepted by the click handler and don't need
        // to be included as exceptions here
        // Don't include the app or map in the elements with ids array
        if (id === 'app' || id === 'map') return false;
        // Don't include ids that have colons so that the wetland layer doesn't cause problems
        if (id.includes(':')) return false;
        // If id exists
        return id;
      });

    // If any elements exist where clicked, execute the handler
    if (!clickedFeatureIds.length) return;

    clickedFeatureIds.forEach((featureId) => {
      // featureId can be a superset of the uid, so find the "root" uid
      // ex: featureId = 'building123-top', uid = 'building123'
      const matchingId = Array.from(this.uidMap.keys()).find((uid) => featureId.includes(uid));
      const featureHandlers = this.uidMap.get(matchingId);
      featureHandlers?.forEach((handler, handlerName) => {
        handler(event);
      });
    });
  }
}

// Beyond Maps will export & use a single instance of EventService.
/** @type {EventService} */
let service;
const initialize = () => service = new EventService();

export {
  service as EventService,
  initialize,
  // For testing
  EventService as EventServiceClass,
};
